import { useState, useMemo } from "react";

export const formatDate = (epochSeconds) => {
  if (!epochSeconds) return "-";
  // Attempt to parse as epoch if it's a number, else try to use it directly
  const d = new Date(typeof epochSeconds === "number" ? epochSeconds * 1000 : epochSeconds);
  if (isNaN(d.getTime())) return epochSeconds;
  
  const day = String(d.getDate()).padStart(2, '0');
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[d.getMonth()];
  const year = d.getFullYear();
  
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const strHours = String(hours).padStart(2, '0');
  
  return `${day} ${month} ${year} ${strHours}:${minutes} ${ampm}`;
};

export const safeBase64Decode = (value) => {
  try {
    return atob(value);
  } catch {
    return value;
  }
};

export function useTableState(rows) {
  /* ---------------- FILTER STATE ---------------- */
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: "",
    toDate: "",
  });

  const [isSearchApplied, setIsSearchApplied] = useState(false);

  /* ---------------- TABLE STATE ---------------- */
  const [currentPage, setCurrentPage] = useState(1);
  const [sortAsc, setSortAsc] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  /* ---------------- FILTER LOGIC ---------------- */
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const createdDate = row.created_at ? new Date(typeof row.created_at === "number" ? row.created_at * 1000 : row.created_at) : null;

      const fromMatch = appliedFilters.fromDate
        ? createdDate >= new Date(appliedFilters.fromDate)
        : true;

      const toMatch = appliedFilters.toDate
        ? createdDate <= new Date(`${appliedFilters.toDate}T23:59:59`)
        : true;

      return fromMatch && toMatch;
    });
  }, [rows, appliedFilters]);

  /* ---------------- SORT LOGIC ---------------- */
  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const d1 = new Date(a.created_at ? (typeof a.created_at === "number" ? a.created_at * 1000 : a.created_at) : 0);
      const d2 = new Date(b.created_at ? (typeof b.created_at === "number" ? b.created_at * 1000 : b.created_at) : 0);
      return sortAsc ? d1 - d2 : d2 - d1;
    });
  }, [filteredRows, sortAsc]);

  /* ---------------- PAGINATION LOGIC ---------------- */
  const totalPages = Math.ceil(sortedRows.length / pageSize);
  const paginatedRows = sortedRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const startEntry = sortedRows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, sortedRows.length);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  /* ---------------- ACTIONS ---------------- */
  const handleSearch = () => {
    setAppliedFilters({ fromDate, toDate });
    setCurrentPage(1);
    setIsSearchApplied(true);
  };

  const handleCancel = () => {
    setFromDate("");
    setToDate("");
    setAppliedFilters({ fromDate: "", toDate: "" });
    setCurrentPage(1);
    setIsSearchApplied(false);
  };

  return {
    fromDate, setFromDate,
    toDate, setToDate,
    isSearchApplied,
    currentPage, setCurrentPage,
    sortAsc, setSortAsc,
    pageSize, setPageSize,
    handleSearch, handleCancel,
    sortedRows, paginatedRows,
    totalPages, startEntry, endEntry, pages
  };
}
